import { Button, Input, Card } from "antd";
import React, { useEffect, useState } from "react";
import { supabase } from "../../../../../config/supabaseClient";
import { sendWelcomeEmailToSubscriber } from "../../../../../Utils/SupabaseServices/EmailService";
import { useTemplateAnalyticsOptional } from "../../../../DashboardView/MasterTemplateEditor/TemplateAnalyticsContext";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import {
  bannerImageFrameStyle,
  normalizeBannerImageFrame,
} from "../../../../DashboardView/MasterTemplateEditor/templateBannerUtils";
import "../../../EmailFlow/EmailFlow.css";

type CampaignFormField = { label?: string; required?: boolean };

const CAMPAIGN_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmailFieldLabel(label: string): boolean {
  return label.trim().toLowerCase() === "email";
}

function campaignFieldKey(field: CampaignFormField, index: number): string {
  const label = String(field.label || "").trim();
  return label || `field-${index}`;
}

function readCampaignFormFields(data: any): CampaignFormField[] {
  try {
    const details =
      typeof data?.details === "string" ? JSON.parse(data.details) : data?.details;
    if (Array.isArray(details?.fields)) return details.fields;
  } catch {
    /* details are optional */
  }
  return [];
}

function validateCampaignField(field: CampaignFormField, value: string): string {
  const label = String(field.label || "this field").trim() || "this field";
  if (field.required && !value.trim()) {
    return `Please enter ${label}`;
  }
  if (isEmailFieldLabel(label) && value.trim() && !CAMPAIGN_EMAIL_REGEX.test(value.trim())) {
    return "Please enter a valid email address";
  }
  return "";
}


const CampaignWrapper: React.FC<any> = ({
  data,
  user_id,
  brand_color,
  isDashboard = false,
  from,
  layout = "inline",
}) => {
  const templateAnalytics = useTemplateAnalyticsOptional();
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [campaignData, setCampaignData] = useState({
    campaignName: "Join For Weekly",
    buttonText: "Subscribe Now",
    campaignId: "",
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    let campaignName = data.campaign_name || data.name || "Join For Weekly";
    let buttonText = data.campaign_button_text || "Subscribe Now";
    let campaignId = data.id || "";

    try {
      const details =
        typeof data.details === "string"
          ? JSON.parse(data.details)
          : data.details;

      if (details) {
        campaignName = details.campaign_name || campaignName;
        buttonText = details.campaign_button_text || buttonText;
        campaignId = details.campaign_id || campaignId;
      }
    } catch (error) {
      console.warn("Error parsing campaign details:", error);
    }

    setCampaignData({ campaignName, buttonText, campaignId });
  }, [data]);

  // Update isMobile on window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async () => {
    setErrorMsg("");
    const fields = readCampaignFormFields(data);
    const nextErrors: Record<string, string> = {};
    fields.forEach((field, index) => {
      const key = campaignFieldKey(field, index);
      const err = validateCampaignField(field, fieldValues[key] || "");
      if (err) nextErrors[key] = err;
    });
    if (fields.length === 0) {
      setErrorMsg("Please enter email.");
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    const emailFieldIndex = fields.findIndex((field) =>
      isEmailFieldLabel(String(field.label || "")),
    );
    const emailKey =
      emailFieldIndex >= 0
        ? campaignFieldKey(fields[emailFieldIndex], emailFieldIndex)
        : "";
    const normalizedEmail = (emailKey ? fieldValues[emailKey] || "" : "")
      .trim()
      .toLowerCase();

    if (!campaignData.campaignId) {
      setErrorMsg("Campaign ID not found.");
      return;
    }

    setLoading(true);
    const dynamicFieldData = fields.map((field, index) => ({
      [String(field.label || campaignFieldKey(field, index))]:
        fieldValues[campaignFieldKey(field, index)] || "",
    }));

    // Same subscriber shape as the Email Campaigns form (details holds the email).
    const { data: newSubscriber, error } = await supabase
      .from("campaign_subscriber")
      .insert([
        {
          user_id,
          details: normalizedEmail,
          campaign_id: campaignData.campaignId,
          campaign_name: campaignData.campaignName,
          other_details:
            dynamicFieldData.length > 0
              ? JSON.stringify(dynamicFieldData)
              : null,
          email_sent: false,
          email_type: "welcome",
        },
      ])
      .select();

    if (error) {
      console.error("Supabase insert error:", error);
      setErrorMsg("Failed to subscribe. Please try again.");
      setLoading(false);
      return;
    }

    // Prefer welcome_* template (same as CamapignForm / LiB), fallback to legacy fields
    if (newSubscriber && newSubscriber[0]) {
      try {
        const [campaignResult, userResult] = await Promise.allSettled([
          supabase
            .from("creators_campaigns")
            .select("*")
            .eq("id", campaignData.campaignId)
            .single(),
          supabase
            .from("user")
            .select("name, email")
            .eq("user_id", user_id)
            .single(),
        ]);

        const campaign =
          campaignResult.status === "fulfilled"
            ? campaignResult.value.data
            : null;
        const userData =
          userResult.status === "fulfilled" ? userResult.value.data : null;

        const welcomeSubject =
          campaign?.welcome_email_subject || campaign?.email_subject || "";
        const welcomeBody =
          campaign?.welcome_email_body || campaign?.email_body || "";

        if (campaign && welcomeBody) {
          const subscriberData = {
            ...newSubscriber[0],
            details: normalizedEmail,
          };

          const campaignWithWelcomeTemplate = {
            ...campaign,
            email_subject: welcomeSubject,
            email_body: welcomeBody,
          };

          const emailResult = await sendWelcomeEmailToSubscriber(
            subscriberData,
            campaignWithWelcomeTemplate,
            userData?.name,
            userData?.email,
          );

          if (!emailResult.success) {
            console.error("Failed to send welcome email:", emailResult.message);
          }
        }
      } catch (emailError) {
        console.error("Error sending welcome email:", emailError);
      }
    }

    setLoading(false);
    setFieldValues({});
    setFieldErrors({});
    setErrorMsg("");
    setSuccess(true);
    window.setTimeout(() => setSuccess(false), 2500);
    templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.NEWSLETTER_SUBMIT, {
      sectionId: "newsletter",
      sectionType: "NewsletterSection",
      entityType: "form",
      entityId: campaignData.campaignId,
      entityLabel: campaignData.campaignName,
      details: { campaign_id: campaignData.campaignId, campaign_name: campaignData.campaignName },
    });
  };

  const stacked = layout === "stacked";
  let imageUrl = data.image;
  let imageFrameStyle: React.CSSProperties | undefined;
  let formFields: Array<{ label?: string; required?: boolean }> = [];
  try {
    const details =
      typeof data.details === "string" ? JSON.parse(data.details) : data.details;
    if (details?.image) imageUrl = details.image;
    if (Array.isArray(details?.fields)) formFields = details.fields;
    imageFrameStyle = bannerImageFrameStyle(normalizeBannerImageFrame(details));
  } catch {
    imageUrl = data.image;
  }

  const fieldsAndButton = (
    <div className={!stacked && !imageUrl ? "campaign-fields-inline" : undefined}>
      {formFields.map((field, idx) => {
        if (!String(field.label || "").trim()) return null;
        const key = campaignFieldKey(field, idx);
        const fieldError = fieldErrors[key];
        return (
        <div key={key} className="campaign-field" style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            {field.label}
            {field.required ? (
              <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>
            ) : null}
          </label>
          <Input
            placeholder={field.label}
            value={fieldValues[key] || ""}
            disabled={isDashboard || success}
            status={fieldError ? "error" : undefined}
            className="campaign-form-input"
            onChange={(e) => {
              const value = e.target.value;
              setFieldValues((prev) => ({ ...prev, [key]: value }));
              if (fieldErrors[key]) {
                const next = validateCampaignField(field, value);
                setFieldErrors((prev) => ({ ...prev, [key]: next }));
              }
            }}
            style={{
              borderRadius: 8,
              backgroundColor: "#fff",
              color: "#111",
              cursor: isDashboard || success ? "not-allowed" : "text",
            }}
          />
          <div
            className={`campaign-form-error-slot${fieldError ? " campaign-form-error-msg" : ""}`}
            role={fieldError ? "alert" : undefined}
            aria-hidden={fieldError ? undefined : true}
          >
            {fieldError ? (
              <>
                <span>•</span>
                <span>{fieldError}</span>
              </>
            ) : null}
          </div>
        </div>
        );
      })}
      <Button
        block={stacked || Boolean(imageUrl)}
        loading={loading}
        disabled={isDashboard || success}
        onClick={handleSubmit}
        /* Colors come from `.mte-tpl-nl-campaign-embed` / LiB embed CSS so
           editor preview matches live (incl. accent shell contrast). */
        className={
          success
            ? "campaign-subscribe-btn campaign-success-btn"
            : "campaign-subscribe-btn"
        }
        style={{
          cursor: isDashboard || success ? "not-allowed" : "pointer",
          maxWidth: stacked ? 400 : undefined,
          transition:
            "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, opacity 0.2s ease",
          ...(success
            ? {
                color: "#2e7d32",
                background: "#c8e6c9",
                borderColor: "#c8e6c9",
              }
            : null),
        }}
      >
        {success ? "Subscribed!" : campaignData.buttonText}
      </Button>
      <div
        className={`campaign-form-error-slot${errorMsg ? " campaign-form-error-msg" : ""}`}
        style={{ marginTop: 4 }}
        role={errorMsg ? "alert" : undefined}
        aria-hidden={errorMsg ? undefined : true}
      >
        {errorMsg ? (
          <>
            <span>•</span>
            <span>{errorMsg}</span>
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      className={`campaign-card-container${stacked ? " is-stacked" : " is-inline"}`}
      style={{
        // backgroundColor: brand_color,
        // boxShadow: `${brand_color} 0px 0.325rem 0px 0px`,
      }}
    >
      <Card
      className="campaign-card"
        style={{
          borderRadius: 16,
          padding: 0,
          border: "none",
        }}
      >
        {imageUrl ? (
          <>
            {isMobile || stacked ? (
              <p className="campaign-name">{campaignData.campaignName}</p>
            ) : null}
            <div className="campaign-image-container">
              <div className="campaign-image-frame">
                <img
                  src={imageUrl}
                  alt=""
                  className="campaign-image"
                  style={imageFrameStyle}
                />
              </div>
              <div className="campaign-form-container-right">
                {!isMobile && !stacked ? (
                  <p className="campaign-name">{campaignData.campaignName}</p>
                ) : null}
                <div style={{ width: "100%" }}>{fieldsAndButton}</div>
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              borderRadius: 16,
            }}
          >
            <p className="campaign-name">{campaignData.campaignName}</p>
            <div style={{ width: "100%" }}>{fieldsAndButton}</div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CampaignWrapper;